import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild, ElementRef, ChangeDetectorRef, Renderer2 } from '@angular/core';
import { NgForm, NgModelGroup, FormBuilder } from '@angular/forms';
import { MatTabGroup } from '@angular/material/tabs';
import { Router, ActivatedRoute } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
// import { faSearch } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { CountryCode } from 'libphonenumber-js';
import { ViewTableComponent } from '../../base/view-table/view-table.component';
import { BaseDialogComponent } from '../base-dialog/base-dialog.component';
import { BaseComponent } from '../base-form/base-form.component';
import { PackagesService } from 'src/app/services/packages.service';
import { InternationalAddressService } from 'src/app/services/international-address.service';
import { PhoneNumberService } from 'src/app/services/phone-number.service';
import { RefreshService } from 'src/app/services/refresh.service';
import { DialogService } from 'src/app/services/dialog.service';
import { EntityRESTService } from 'src/app/services/entity-rest.service';
import { UtilityService } from 'src/app/services/utility.service';
import { FieldsService } from 'src/app/services/fields.service';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { TaxonomyService } from 'src/app/services/taxonomy.service';
import { AuthRESTService } from 'src/app/services/auth-rest.service';
import { AuthService } from 'src/app/services/auth.service';
import { MenuRESTService } from 'src/app/services/menu-rest.service';
import { RouteEventsService } from 'src/app/route-events.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { ConfigPagesService } from 'src/app/services/config-pages.service';
import { UserService } from 'src/app/services/user.service';
import { StudioConfigService } from 'src/app/services/studio-config.service';
import { ComponentRefreshService } from 'src/app/services/component-refresh.service';
import { LoggerService } from '../../../services/logger.service';

/**
 * Creates a student account. The contact section works the way the student
 * inquiry form's does: one student, plus a partner when they attend together,
 * each with the same fields and address handling.
 */
@Component({
  selector: 'app-student-account-entity',
  templateUrl: './student-account-entity.component.html',
  styleUrls: ['./student-account-entity.component.css']
})
export class StudentAccountEntityComponent extends BaseComponent implements OnInit, AfterViewInit {
  eckType = "student_accounts";
  bundle = "student_account";
  entityId = '';

  inquiry = false;

  @ViewChild('firstContact') firstContact: NgModelGroup;
  @ViewChild('partnerContact') partnerContact: NgModelGroup;

  // Hosted inside another dialog (gift certificate redemption): no page
  // header, and the created account is handed back instead of navigating.
  @Input() embedded = false;
  // Seeds the student's contact so the form opens with the person already filled in.
  @Input() prefillContact: any = null;
  /** Seeds the Inquiry Taker select (a user id) when embedded, e.g. the executive who sold a gift certificate. */
  @Input() prefillInquiryTaker: string = null;
  @Output() studentCreated = new EventEmitter<{ studentId: string; contactId: string }>();
  /** Fired when embedded creation fails, so a host can release its own busy state. */
  @Output() studentCreateFailed = new EventEmitter<any>();

  field_attending_lessons: string = 'solo';
  field_communication_opt_in: boolean = false;
  field_communication_opt_in_partner: boolean = false;

  duplicateWarning: string = '';
  duplicateWarningPartner: string = '';

  // Address internationalization
  selectedCountry = 'US';
  selectedCountryPartner = 'US';
  administrativeAreaLabel = 'State';
  postalCodeLabel = 'ZIP Code';
  administrativeAreaLabelPartner = 'State';
  postalCodeLabelPartner = 'ZIP Code';

  // The studio's own country (separate from the editable address country below).
  studioCountry = '';

  // Fiscal Code (Codice Fiscale) is editable when the studio OR the student's
  // address country is Italy/Spain. Reactive to the address country dropdown.
  get showFiscalCode(): boolean {
    return ['IT', 'ES'].includes(this.studioCountry)
      || ['IT', 'ES'].includes(this.selectedCountry);
  }

  constructor(
    public internationalAddressService: InternationalAddressService,
    public phoneNumberService: PhoneNumberService,
    // All base component dependencies
    _refreshService: RefreshService,
    _dialogService: DialogService,
    _entityRESTService: EntityRESTService,
    _utilityService: UtilityService,
    _fieldsService: FieldsService,
    _drupalRESTService: DrupalRESTService,
    _taxonomyService: TaxonomyService,
    _router: Router,
    _activatedRoute: ActivatedRoute,
    _authRESTService: AuthRESTService,
    _authService: AuthService,
    _menuRESTService: MenuRESTService,
    _formBuilder: FormBuilder,
    _routeEventsService: RouteEventsService,
    _permissionsService: PermissionsService,
    _renderer2: Renderer2,
    _configPages: ConfigPagesService,
    _packagesService: PackagesService,
    _snackBar: MatSnackBar,
    _userRESTService: UserService,
    _el: ElementRef,
    _changeDetectorRef: ChangeDetectorRef,
    _sanitizer: DomSanitizer,
    _studioConfigService: StudioConfigService,
    _componentRefreshService: ComponentRefreshService,
    _translateService: TranslateService
  ) {
    super(
      _refreshService,
      _dialogService,
      _entityRESTService,
      _utilityService,
      _fieldsService,
      _drupalRESTService,
      _taxonomyService,
      _router,
      _activatedRoute,
      _authRESTService,
      _authService,
      _menuRESTService,
      _formBuilder,
      _routeEventsService,
      _permissionsService,
      _renderer2,
      _configPages,
      _packagesService,
      _snackBar,
      _userRESTService,
      _el,
      _changeDetectorRef,
      _sanitizer,
      _studioConfigService,
      _componentRefreshService,
      _translateService
    );
  }

  /**
   * Handle country change to update labels
   */
  onCountryChange(countryCode: string) {
    this.selectedCountry = countryCode;
    this.administrativeAreaLabel = this.internationalAddressService.getAdministrativeAreaLabel(countryCode);
    this.postalCodeLabel = this.internationalAddressService.getPostalCodeLabel(countryCode);
  }

  /**
   * Handle partner country change to update labels
   */
  onCountryChangePartner(countryCode: string) {
    this.selectedCountryPartner = countryCode;
    this.administrativeAreaLabelPartner = this.internationalAddressService.getAdministrativeAreaLabel(countryCode);
    this.postalCodeLabelPartner = this.internationalAddressService.getPostalCodeLabel(countryCode);
  }

  onAttendingLessonsChange(event: any) {
    this.field_attending_lessons = event.value;
  }

  onCommunicationOptInChange(value: boolean) {
    this.field_communication_opt_in = value;
  }

  onCommunicationOptInPartnerChange(value: boolean) {
    this.field_communication_opt_in_partner = value;
  }

  /**
   * Whether the contact section can be submitted: the student is complete,
   * and so is the partner when they attend together.
   */
  get contactsValid(): boolean {
    if (!this.firstContact?.valid) {
      return false;
    }
    return this.field_attending_lessons !== 'partner' || !!this.partnerContact?.valid;
  }

  /**
   * Whether the account can be created: complete contacts and an inquiry
   * taker, the two things the server rejects the post without.
   */
  get canSubmit(): boolean {
    return this.contactsValid && !!this.f?.form?.get('field_inquiry_taker')?.value;
  }

  /** One-line address for a collapsed panel header, or '' when nothing has been entered. */
  addressSummary(address: any): string {
    if (!address) {
      return '';
    }
    const street = (address.address_line1 || '').trim();
    const region = [address.locality, address.administrative_area, address.postal_code]
      .map((part: any) => (part || '').toString().trim())
      .filter(Boolean)
      .join(', ');
    return [street, region].filter(Boolean).join(', ');
  }

  copyAddress() {
    if (this.firstContact && this.partnerContact) {
      const firstContactAddress = this.firstContact.control.get('field_address').value;
      this.partnerContact.control.get('field_address').setValue({
        multiple_properties: 'true',
        address_line1: firstContactAddress?.address_line1,
        locality: firstContactAddress?.locality,
        administrative_area: firstContactAddress?.administrative_area,
        postal_code: firstContactAddress?.postal_code,
        country_code: firstContactAddress?.country_code || this.selectedCountry,
      });
    }
  }

  checkDuplicateStudent(contactType: 'main' | 'partner') {
    const formGroup = contactType === 'main' ? this.firstContact : this.partnerContact;
    const firstName = formGroup?.control.get('field_first_name')?.value;
    const lastName = formGroup?.control.get('field_last_name')?.value;

    if (!firstName || !lastName) {
      return;
    }

    const params = [
      { parameter: 'field_first_name', value: firstName },
      { parameter: 'field_last_name', value: lastName }
    ];

    this._drupalRESTService.httpGET('/api_rest/v1/checkDuplicateStudent', params).subscribe(
      (response: any) => {
        const warning = response?.warning || '';
        if (contactType === 'main') {
          this.duplicateWarning = warning;
        } else {
          this.duplicateWarningPartner = warning;
        }
      },
      error => {
        LoggerService.error('Error checking for duplicate student:', error);
      }
    );
  }

  override onSubmit(f: NgForm) {
    let values = f.form.value;

    // Alter certain date fields to match what Drupal is looking for.
    if (values.field_inquired) { values.field_inquired = moment(values.field_inquired).format('YYYY-MM-DD'); }
    if (values.field_showed) { values.field_showed = moment(values.field_showed).format('YYYY-MM-DD'); }
    if (values.field_booked_on) { values.field_booked_on = moment(values.field_booked_on).format('YYYY-MM-DD'); }

    // An empty teacher is rejected as required even though the field is
    // optional, so leave it out, as the inquiry form does.
    if (values.field_teacher == "") { delete values.field_teacher; }

    const partner = this.field_attending_lessons === 'partner'
      ? this.buildContact(this.partnerContact, 'partner')
      : null;

    // Prep the data to be send.
    let body = {
      ...values,
      field_contacts: [this.buildContact(this.firstContact, 'main'), partner].filter(contact => contact !== null),
      field_archive: "0",
      title: "Default Title"
    };

    this.displayProgressSpinner(true);

    this._entityRESTService.createEntity('student_accounts', 'student_account', body)
      .subscribe(data => {
        this.displayProgressSpinner(false);

        if (this.embedded) {
          // The account id is the entity id; the navigate below uses the
          // contact id, which is a different entity.
          this.studentCreated.emit({
            studentId: data?.['id']?.[0]?.value,
            contactId: data?.['field_contacts']?.[0]?.target_id,
          });
          return;
        }

        // Land on the students list with the new student's dashboard open; the
        // dialog opens after navigating since dialogs close on navigation.
        const contactID = data['field_contacts'][0].target_id;
        this._router.navigate(['/students-list']).then(() => this.openStudentDashboardDialog(contactID));
      },
        error => {
          this.handleError(error);
          if (this.embedded) {
            this.studentCreateFailed.emit(error);
          }
        }
      )

  }

  /**
   * Shapes one contact group's values the way the inquiry form does. The
   * studio goes on the contact itself: the API builds contacts before it has
   * read the account's studio, so they cannot inherit it.
   */
  private buildContact(group: NgModelGroup, which: 'main' | 'partner') {
    const value = group?.value || {};
    const country = which === 'main' ? this.selectedCountry : this.selectedCountryPartner;
    const optIn = which === 'main' ? this.field_communication_opt_in : this.field_communication_opt_in_partner;
    const gender = which === 'main' ? value.field_gender_main : value.field_gender_partner;
    const address = value.field_address || {};
    const hasAddress = address.address_line1 || address.locality || address.administrative_area || address.postal_code;

    const contact: any = {
      type: 'contacts',
      bundle: 'student_record',
      field_attending_lessons: this.field_attending_lessons,
      field_first_name: value.field_first_name,
      field_last_name: value.field_last_name,
      field_email: value.field_email,
      field_home_phone: this.phone(value.field_home_phone, country),
      field_cell_phone: this.phone(value.field_cell_phone, country),
      field_lead_follow: which === 'main' ? value.field_lead_follow : value.field_lead_follow_partner,
      field_communication_opt_in: optIn ? "1" : "0",
    };

    const studioId = this._authService.selectedStudio?.id;
    if (studioId) {
      contact.field_studio_reference = [studioId];
    }

    if (value.field_fiscal_code) {
      contact.field_fiscal_code = value.field_fiscal_code;
    }

    // Only include gender if it has a value (it's a boolean field)
    if (gender !== '' && gender !== null && gender !== undefined) {
      contact.field_gender = gender;
    }

    // Only include address if at least one field is filled
    if (hasAddress) {
      contact.field_address = {
        address_line1: address.address_line1,
        locality: address.locality,
        administrative_area: address.administrative_area,
        postal_code: address.postal_code,
        country_code: address.country_code || country,
      };
    }

    return contact;
  }

  /** E.164 from the international phone input, or from a plain string. */
  private phone(raw: any, country: string): string {
    if (raw && typeof raw === 'object') {
      return raw.e164Number || '';
    }
    if (typeof raw === 'string' && raw) {
      return this.phoneNumberService.parseNumber(raw, country as CountryCode).e164 || raw;
    }
    return '';
  }

  ngOnInit(): void {
    this.initActionType = "create";
    this.setFormMode("create");

    // Load studio country to default the address country field
    this.loadStudioCountry();
  }

  ngAfterViewInit(): void {
    if (this.prefillContact || this.prefillInquiryTaker) {
      // Template-driven controls register after the view is created, so the
      // seed has to wait a tick before it can land on them.
      setTimeout(() => {
        if (this.prefillContact) {
          this.firstContact?.control.patchValue(this.prefillContact);
        }
        if (this.prefillInquiryTaker) {
          this.f?.form?.get('field_inquiry_taker')?.setValue(this.prefillInquiryTaker);
        }
      });
    }
  }

  /**
   * Load the studio's country setting to use as default for student addresses
   */
  loadStudioCountry(): void {
    const configFieldName = 'field_studio_information_ref';
    this._drupalRESTService
      .httpGET(`/api_rest/v1/loadStudioConfig?config_field_name=${configFieldName}`)
      .subscribe(
        (response) => {
          const studioCountry = response?.['field_studio_address']?.[0]?.country_code;
          if (studioCountry) {
            this.studioCountry = studioCountry;
            this.selectedCountry = studioCountry;
            this.selectedCountryPartner = studioCountry;
            this.onCountryChange(studioCountry);
            this.onCountryChangePartner(studioCountry);
          }
        },
        (error) => {
          LoggerService.error('Error loading studio country:', error);
          // Keep default US if loading fails
        }
      );
  }

}
